/** @format */

import React from "react";
import { v4 as uuidv4 } from "uuid";
import Slider from "react-slick";
import Lottie from "lottie-react";
import rocket from "../../img/rocket-white.json";
import error404 from "../../img/error-glitch.json";
import BookingDesktop from "./BookingDesktop";
import BookingMobile from "./BookingMobile.jsx";
import BookingMobileNextMonth from "./BookingMobileNextMonth";
import BookingDesktopNextMonth from "./BookingDesktopNextMonth.jsx";

//material ui
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useLocation } from "react-router-dom";
import useTranslation from "../../hooks/useTranslation";
import { useCourses } from "../../context/CourseContext";

const useStyles = (theme, isMobile) => ({
  container: {
    display: "flex",
    flexDirection: { xs: "column" },
    alignItems: { xs: "center" },
    mt: { xs: "125px" },
  },
  sliderContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflowY: "visible",
    marginTop: "30px",
  },
  slider: {
    marginTop: isMobile ? "0" : "110px",
    width: "98%",
    height: "fit-content",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
  },
  line: {
    width: "25px",
    border: `1px solid ${theme.palette.red.main}`,
    marginRight: "8px",
    height: 0,
    backgroundColor: theme.palette.red.main,
  },
  titleContainer: {
    display: { md: "flex" },
    justifyContent: { md: "center" },
    alignItems: { md: "center" },
  },

  applySecond: {
    width: "293px",
    height: "fit-content",
    borderRadius: "47px",
    border: "solid 5px #fff",
    backgroundImage: "linear-gradient(to bottom, #fcfdfe 0%, #fcfdff 100%)",
    marginTop: "82px",
    background: "linear-gradient(to bottom, #fcfdfe 0%, #fcfdff 100%)",
    boxShadow: "0 4px 92px 0 rgba(0, 31, 85, 0.06)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    overflow: "hidden",
  },
  typography: {
    section: {
      fontSize: "1rem",
      fontWeight: "bold",
      letterSpacing: "2px",
      color: theme.palette.red.main,
      marginTop: isMobile ? "59px" : "0",
    },
    sectionDescription: {
      fontSize: "3rem",
      textAlign: "center",
      fontFamily: theme.typography.montserrat.regular,
      color: theme.palette.blue.main,
      marginTop: isMobile ? "24px" : "10px",
      width: isMobile ? "170px" : "fit-content",
    },
    descriptionSubtitle: {
      fontSize: isMobile ? 14 : 16,
      fontWeight: "bold",
      letterSpacing: "2px",
      color: theme.palette.red.main,
      marginBottom: "0.6rem",
    },
    descriptionTitle: {
      fontSize: isMobile ? 32 : 65,
      fontWeight: "lighter",
      fontFamily: theme.typography.montserrat.regular,
      color: theme.palette.blue.main,
      lineHeight: "43px",
    },
    title: {
      color: theme.palette.red.main,
      fontSize: { xs: "14px", md: "16px" },
      letterSpacing: { xs: "1.4px", md: "1.6px" },
      fontFamily: theme.typography.montserrat.semibold,
    },
    text: {
      fontFamily: theme.typography.anaHeim.regular,
      fontSize: 24,
      margin: "15px 15px",
    },
  },
});

const lottieStyle = {
  height: 300,
};

const Schedule = ({ courses }) => {
  const isMobile = useMediaQuery("(max-width:899px)");
  const theme = useTheme();
  const classes = useStyles(theme, isMobile);
  const { pathname } = useLocation();
  const componentName = "Schedule";
  const translation = useTranslation(pathname, componentName);
  const { title, subtitle, errorLoadingCourses } = translation;
  const { loading, error } = useCourses();

  if (loading) {
    return isMobile ? (
      <Box sx={classes.container}>
        <Typography sx={classes.typography.section}>{title}</Typography>
        <Typography sx={classes.typography.sectionDescription}>
          {subtitle[0]} <b>{subtitle[1]}</b>
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Lottie animationData={rocket} loop={true} style={lottieStyle} />
          </Box>
        </Box>
      </Box>
    ) : (
      <Box sx={classes.container}>
        <Box sx={classes.titleContainer}>
          <div style={classes.line}></div>
          <Typography sx={classes.typography.section}>{title}</Typography>
        </Box>
        <Typography sx={classes.typography.sectionDescription}>
          {subtitle[0]} <b>{subtitle[1]}</b>
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Lottie animationData={rocket} loop={true} style={lottieStyle} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-evenly",
          alignItems: "center",
          flexDirection: isMobile ? "column" : null,
          margin: "1rem",
        }}
      >
        <Box>
          <Lottie animationData={error404} loop={true} style={lottieStyle} />
        </Box>
        <Box
          sx={{
            flexWrap: "wrap",
            maxWidth: "40%",
          }}
        >
          <Typography
            sx={{
              fontFamily: theme.typography.anaHeim.regular,
              fontSize: "clamp(1rem, 2vw, 1.5rem)",
              color: theme.palette.red.main,
            }}
          >
            {errorLoadingCourses}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={classes.container}>
      {courses && (
        <>
          <Box sx={classes.titleContainer}>
            {isMobile ? null : <div style={classes.line}></div>}
            <Typography sx={classes.typography.title}>{title}</Typography>
          </Box>
          <Typography
            sx={{
              ...classes.typography.descriptionTitle,
              margin: "1rem 0 2.5rem 0",
            }}
          >
            {subtitle[0]} <b>{subtitle[1]}</b>
          </Typography>

          {courses
            .sort((prev) => prev.status==="disponible" ? -1 : 1)
            .map((course) =>
              course.status === "disponible" ? (
                isMobile ? (
                  <Box style={classes.sliderContainer} key={uuidv4()}>
                    <Slider
                      slidesToShow={1}
                      style={classes.slider}
                      autoplay={true}
                      autoplaySpeed={4000}
                      dots={true}
                    >
                      <BookingMobile
                        key={uuidv4()}
                        info={course}
                        startDate={course.startDate}
                        endDate={course.endDate}
                        startHour={course.startHour}
                        endHour={course.endHour}
                        shift={course.shift}
                        weekDays={course.weekDays}
                        month={course.month}
                        year={course.year}
                        limitToApply={course.limitToApply}
                        avialability={course.availability}
                        bootcampPreparation={course.bootcampPreparation}
                        name={course.type.name}
                        tag={course.type.tag}
                      />
                    </Slider>
                  </Box>
                ) : (
                  <BookingDesktop
                    key={uuidv4()}
                    info={course}
                    startDate={course.startDate}
                    endDate={course.endDate}
                    startHour={course.startHour}
                    endHour={course.endHour}
                    shift={course.shift}
                    weekDays={course.weekDays}
                    month={course.month}
                    year={course.year}
                    limitToApply={course.limitToApply}
                    avialability={course.availability}
                    bootcampPreparation={course.bootcampPreparation}
                    name={course.type.name}
                    tag={course.type.tag}
                  />
                )
              ) : isMobile ? (
                <BookingMobileNextMonth
                  key={uuidv4()}
                  info={course}
                  startDate={course.startDate}
                  endDate={course.endDate}
                  startHour={course.startHour}
                  endHour={course.endHour}
                  shift={course.shift}
                  weekDays={course.weekDays}
                  month={course.month}
                  year={course.year}
                  limitToApply={course.limitToApply}
                  avialability={course.availability}
                  bootcampPreparation={course.bootcampPreparation}
                  name={course.type.name}
                  tag={course.type.tag}
                />
              ) : (
                <BookingDesktopNextMonth
                  key={uuidv4()}
                  info={course}
                  startDate={course.startDate}
                  endDate={course.endDate}
                  startHour={course.startHour}
                  endHour={course.endHour}
                  shift={course.shift}
                  weekDays={course.weekDays}
                  month={course.month}
                  year={course.year}
                  limitToApply={course.limitToApply}
                  avialability={course.availability}
                  bootcampPreparation={course.bootcampPreparation}
                  name={course.type.name}
                  tag={course.type.tag}
                />
              )
            )}
        </>
      )}
    </Box>
  );
};

export default Schedule;
